import { FC, useMemo, useState } from "react"
import { Form, Formik, useFormikContext } from "formik"
import * as Yup from "yup"
import {
  Activity,
  ChevronDown,
  Cpu,
  Layers,
  Palette,
  Sliders,
  User
} from "lucide-react"
import { Integration } from "../../../../types"
import If from "../../if"
import {
  REGEX_HEX_COLOR,
  REGEX_HTPPS_URL_WILDCARD_QUERY
} from "../../../constants"
import { normalizeUrl } from "../../../utils"
import {
  Colors,
  WidgetsCheckboxes,
  EmbedsOptions,
  GoogleAnalyticsTag,
  AdvanceOptions,
  IntegrationOwnerSelector
} from "../fields"
import Input from "../../form/input"
import { HELP_TEXT_URL } from "../../../constants"
import { useFormIntegrationRef } from "../manage"
import { useAppSelector } from "../../../hooks"
import { selectUser } from "../../../../redux/slices/auth"

const hexColor = Yup.string().matches(
  REGEX_HEX_COLOR,
  "Is not a valid hex color"
)

const formSchema = Yup.object().shape({
  title: Yup.string().required().min(1),
  accent_color: hexColor,
  secondary_accent_color: hexColor,
  url: Yup.string()
    .transform(normalizeUrl)
    .matches(REGEX_HTPPS_URL_WILDCARD_QUERY, "Invalid URL")
    .required(),
  type: Yup.string().required(),
  owner: Yup.string().required()
})

interface SectionCardProps {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
  right?: React.ReactNode
}

const SectionCard: FC<SectionCardProps> = ({
  icon,
  title,
  right,
  children
}) => (
  <div className="bg-white rounded shadow-sm p-6 [&_label]:text-[11px] [&_label]:font-medium [&_label]:uppercase [&_label]:tracking-wider [&_label]:text-neutral-400 [&_label]:mb-1.5">
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-2">
        <span className="text-violet-400">{icon}</span>
        <span className="text-sm font-medium text-neutral-800">{title}</span>
      </div>
      {right}
    </div>
    {children}
  </div>
)

interface Props {
  integration?: Integration
  isLoading: boolean
  onSaveSettings: (integration: Integration) => void
  open?: boolean
}

const WidgetsSection: FC<{ integration?: Integration }> = ({ integration }) => {
  const { values } = useFormikContext<Integration>()
  if (values.type === "chatbot") return null
  return <WidgetsCheckboxes integration={integration} />
}

const URLInput: FC<{ integration?: Integration }> = ({ integration }) => {
  const { values } = useFormikContext<Integration>()
  return (
    <Input
      label="URL"
      name="url"
      placeholder="Ex. https://google.com/*"
      helpText={values.domains?.length === 0 ? HELP_TEXT_URL : ""}
    />
  )
}

const Settings: FC<Props> = ({
  integration,
  isLoading,
  onSaveSettings,
  open
}) => {
  const user = useAppSelector(selectUser)
  const formRef = useFormIntegrationRef()
  const [showAdvanced, setShowAdvanced] = useState(false)

  const initialValues: Integration = useMemo(() => {
    return integration
      ? {
          ...integration
        }
      : {
          id: "",
          title: "",
          type: "boomtown",
          app_key: "",
          owner: user?.user.id ?? "",
          url: "",
          brand_id: "",
          domains: [],
          app_settings: {
            mort_calc: 0,
            afford_calc: 0,
            pricing: 0,
            lead_workflow: 0,
            pricing_set_id: 0,
            settings: {
              aboutBoxes: false,
              loReviews: false,
              brandReviews: false,
              rateTable: false,
              ga_mode: "",
              ga_tag: "",
              contactForm: 0,
              expose_api: true,
              disableCalcCards: false,
              billing_exempt: false,
              postUrl: "",
              accent_color: "",
              secondary_accent_color: ""
            }
          }
        }
  }, [integration, user?.user.id])

  const showLoader = isLoading && !integration?.id

  return (
    <If
      isLoading={showLoader}
      condition
      then={
        <Formik
          key={String(integration?.id ?? "create")}
          initialValues={initialValues}
          onSubmit={onSaveSettings}
          innerRef={formRef}
          validationSchema={formSchema}
          validateOnBlur
          enableReinitialize={true}
        >
          <Form className="flex flex-col gap-5">
            {/* General Settings */}
            <SectionCard icon={<Sliders size={15} />} title="General Settings">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Input label="Title" name="title" />
                <URLInput integration={integration} />
              </div>
            </SectionCard>

            {/* Embed Type */}
            <SectionCard icon={<Layers size={15} />} title="Embed Type">
              <EmbedsOptions integration={integration} />
            </SectionCard>

            {/* Branding + Analytics */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
              <SectionCard icon={<Palette size={15} />} title="Branding">
                <Colors integration={integration} />
              </SectionCard>
              <SectionCard icon={<Activity size={15} />} title="Analytics">
                <GoogleAnalyticsTag integration={integration} />
              </SectionCard>
            </div>

            {/* Module Configuration */}
            <SectionCard icon={<Cpu size={15} />} title="Module Configuration">
              <WidgetsSection integration={integration} />
            </SectionCard>

            {/* Access & Advanced */}
            <SectionCard
              icon={<User size={15} />}
              title="Access & Advanced"
              right={
                <button
                  type="button"
                  onClick={() => setShowAdvanced(v => !v)}
                  className="flex items-center gap-1.5 text-[13px] text-neutral-400 hover:text-neutral-600 transition-colors select-none"
                >
                  Advanced options
                  <ChevronDown
                    size={14}
                    className={`transition-transform duration-200 ${
                      showAdvanced ? "rotate-180" : ""
                    }`}
                  />
                </button>
              }
            >
              <div className="max-w-sm">
                <IntegrationOwnerSelector integration={integration} />
              </div>
              <AdvanceOptions integration={integration} open={showAdvanced} />
            </SectionCard>
          </Form>
        </Formik>
      }
    />
  )
}

export default Settings
